import { Link } from 'react-router'
import { ChevronRight, Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface BreadcrumbItem {
  label: string
  href?: string
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  showHome?: boolean
  className?: string
}

export function Breadcrumbs({ items, showHome = true, className = '' }: BreadcrumbsProps) {
  const { t } = useTranslation('common')
  const allItems = showHome ? [{ label: t('breadcrumbs.home'), href: '/' }, ...items] : items
  // TC-MUI-A3: breadcrumbs are removed entirely on mobile (screen real estate is
  // scarce and the back affordance + page title already orient the user). They
  // render from `sm` up only. `hidden sm:flex` applies on ALL pages regardless
  // of depth.
  const visibilityClass = 'hidden sm:flex'

  // F8 (EP-158): DaisyUI's `breadcrumbs` class draws its own `::before`
  // separator on every <li> but :not(:first-child), and this component ALSO
  // renders its own ChevronRight before every item after the first — two
  // separators per gap ("› ›"). This component already owns the full item
  // layout (icon, link, spacing), so DaisyUI's class brings nothing but the
  // duplicate separator; dropped rather than removing our own icon, which
  // would lose the Lucide-icon look the rest of the kit uses.
  return (
    <nav className={`text-sm ${visibilityClass} ${className}`}>
      <ul className="flex items-center gap-1">
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1

          return (
            <li key={index} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="w-4 h-4 text-base-content/40" />}
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className="hover:text-primary transition-colors flex items-center gap-1"
                >
                  {index === 0 && showHome && <Home className="w-4 h-4" />}
                  <span>{item.label}</span>
                </Link>
              ) : (
                <span className={`flex items-center gap-1 ${isLast ? 'text-base-content' : 'text-base-content/60'}`}>
                  {index === 0 && showHome && <Home className="w-4 h-4" />}
                  <span>{item.label}</span>
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
